import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  PendingTransactionReturn,
  StatementReturn,
  TransactionReturn,
} from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import {
  formatCurrency,
  formatDate,
  formatDirectionalAmount,
  getBankErrorParts,
} from './utils/bankHelpers';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { Input } from './common/Catalyst/input';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from './common/Catalyst/dialog';
import ShowAlert from './ShowAlert';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import LoadingSpinner from './LoadingSpinner';
import clsx from 'clsx';

// Which account's activity to load. `company` scope addresses the account by
// (companyId, accountNumber); `hoa` scope by the hoa_bank_account assignment id.
export type ActivityScope =
  | { kind: 'company'; companyId: string; accountNumber: string }
  | { kind: 'hoa'; hoaAccountId: string };

interface AccountActivityDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  scope: ActivityScope | null;
}

type Tab = 'transactions' | 'pending' | 'statements';

interface AlertState {
  title: string;
  message: string;
}

const activityError = (err: unknown): AlertState => {
  const { status, body } = getBankErrorParts(err);
  if (status === 502) {
    return {
      title: "Couldn't reach the bank",
      message:
        body ||
        'This data is read live from the bank, which is unreachable from this environment.',
    };
  }
  return {
    title: "Couldn't load activity",
    message: body || 'Please try again.',
  };
};

const AccountActivityDialog: React.FC<AccountActivityDialogProps> = ({
  open,
  onClose,
  title,
  scope,
}) => {
  const [tab, setTab] = useState<Tab>('transactions');
  const [txns, setTxns] = useState<TransactionReturn[] | null>(null);
  // Posted transactions are served from our own copy, so an empty list has
  // three possible meanings with three different remedies. The API tells us
  // which; without this the UI would show "no transactions" for all of them.
  const [sync, setSync] = useState<SyncState | null>(null);
  const [pending, setPending] = useState<PendingTransactionReturn[] | null>(
    null
  );
  const [statements, setStatements] = useState<StatementReturn[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AlertState | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const scopeKey = scope
    ? scope.kind === 'company'
      ? `c:${scope.companyId}:${scope.accountNumber}`
      : `h:${scope.hoaAccountId}`
    : '';

  const loadTransactions = async () => {
    if (!scope) return;
    try {
      setLoading(true);
      setError(null);
      const fromArg = from || undefined;
      const toArg = to || undefined;
      const data =
        scope.kind === 'company'
          ? (
              await bankService.getCompanyAccountTransactions({
                companyId: scope.companyId,
                accountNumber: scope.accountNumber,
                from: fromArg,
                to: toArg,
              })
            ).data
          : (
              await bankService.getHoaAccountTransactions({
                id: scope.hoaAccountId,
                from: fromArg,
                to: toArg,
              })
            ).data;
      setTxns(data.transactions);
      setSync({
        trackingEnabled: data.tracking_enabled,
        lastSyncedAt: data.last_synced_at ?? null,
        coveredFrom: data.covered_from ?? null,
      });
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(activityError(err));
      setTxns([]);
      setSync(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPending = async () => {
    if (!scope) return;
    try {
      setLoading(true);
      setError(null);
      const data =
        scope.kind === 'company'
          ? (
              await bankService.getCompanyAccountPending({
                companyId: scope.companyId,
                accountNumber: scope.accountNumber,
              })
            ).data
          : (
              await bankService.getHoaAccountPending({
                id: scope.hoaAccountId,
              })
            ).data;
      setPending(data.transactions);
    } catch (err) {
      console.error('Error loading pending transactions:', err);
      setError(activityError(err));
      setPending([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatements = async () => {
    if (!scope) return;
    try {
      setLoading(true);
      setError(null);
      const data =
        scope.kind === 'company'
          ? (
              await bankService.getCompanyAccountStatements({
                companyId: scope.companyId,
                accountNumber: scope.accountNumber,
              })
            ).data
          : (
              await bankService.getHoaAccountStatements({
                id: scope.hoaAccountId,
              })
            ).data;
      setStatements(data.statements);
    } catch (err) {
      console.error('Error loading statements:', err);
      setError(activityError(err));
      setStatements([]);
    } finally {
      setLoading(false);
    }
  };

  // On open (or when the account changes), reset and load the first tab.
  useEffect(() => {
    if (!open || !scope) return;
    setTab('transactions');
    setTxns(null);
    // Cleared with the rows: stale sync facts from the previously-viewed
    // account would mislabel this one's empty state.
    setSync(null);
    setPending(null);
    setStatements(null);
    setError(null);
    setFrom('');
    setTo('');
    setDateError(null);
    void loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeKey]);

  const showTab = (t: Tab) => {
    setTab(t);
    setError(null);
    if (t === 'transactions' && txns === null) void loadTransactions();
    if (t === 'pending' && pending === null) void loadPending();
    if (t === 'statements' && statements === null) void loadStatements();
  };

  const applyDates = () => {
    if (to && !from) {
      setDateError('Select a start date to use an end date.');
      return;
    }
    setDateError(null);
    void loadTransactions();
  };

  const downloadStatement = async (s: StatementReturn) => {
    if (!scope) return;
    try {
      setDownloadingId(s.statement_id);
      setError(null);
      const opts = { responseType: 'blob' as const };
      const data =
        scope.kind === 'company'
          ? (
              await bankService.getCompanyStatementFile(
                { companyId: scope.companyId, statementId: s.statement_id },
                opts
              )
            ).data
          : (
              await bankService.getHoaStatementFile(
                { id: scope.hoaAccountId, statementId: s.statement_id },
                opts
              )
            ).data;
      const blob = data as unknown as Blob;
      const ext = (s.file_type || 'pdf').toLowerCase();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement-${s.statement_id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading statement:', err);
      setError(activityError(err));
    } finally {
      setDownloadingId(null);
    }
  };

  const tabButton = (t: Tab, label: string) => (
    <button
      type="button"
      onClick={() => showTab(t)}
      className={clsx(
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        tab === t
          ? 'border-sky-500 text-sky-600 dark:text-sky-400'
          : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
      )}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onClose={onClose} size="4xl">
      <DialogTitle>{title}</DialogTitle>
      <DialogBody>
        <div className="flex gap-2 border-b border-zinc-200 dark:border-zinc-700 mb-4">
          {tabButton('transactions', 'Transactions')}
          {tabButton('pending', 'Pending')}
          {tabButton('statements', 'Statements')}
        </div>

        {tab === 'transactions' && (
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                From
              </label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                To
              </label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-40"
              />
            </div>
            <Button color="sky" onClick={applyDates} disabled={loading}>
              Apply
            </Button>
            {(from || to) && (
              <Button
                plain
                disabled={loading}
                onClick={() => {
                  setFrom('');
                  setTo('');
                  setDateError(null);
                  void loadTransactions();
                }}
              >
                Clear
              </Button>
            )}
            <ErrorAlert>{dateError}</ErrorAlert>
          </div>
        )}

        {error && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle={error.title}
              alertMessage={error.message}
              isVisible={!!error}
              onClose={() => setError(null)}
              timeout={10000}
              isInline={true}
            />
          </div>
        )}

        <div className="min-h-[240px]">
          {loading ? (
            <LoadingSpinner />
          ) : tab === 'transactions' ? (
            <TransactionsTable rows={txns} sync={sync} />
          ) : tab === 'pending' ? (
            <PendingTable rows={pending} />
          ) : (
            <StatementsTable
              rows={statements}
              downloadingId={downloadingId}
              onDownload={downloadStatement}
            />
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const amountClass = (credit: boolean | null): string =>
  credit === true
    ? 'text-green-600 dark:text-green-400'
    : credit === false
      ? 'text-red-600 dark:text-red-400'
      : '';

/** What the API tells us about our own copy of this account's activity. */
type SyncState = {
  trackingEnabled: boolean;
  lastSyncedAt: string | null;
  coveredFrom: string | null;
};

const TransactionsTable: React.FC<{
  rows: TransactionReturn[] | null;
  sync: SyncState | null;
}> = ({ rows, sync }) => {
  if (!rows || rows.length === 0) {
    // Three kinds of empty, three different things to do about them.
    if (sync && !sync.trackingEnabled) {
      return (
        <Text>
          This account is not being tracked, so no transaction history is
          being collected for it. Turn on tracking to start the daily pull.
        </Text>
      );
    }
    if (sync && !sync.lastSyncedAt) {
      return (
        <Text>
          Tracking is on, but this account has not been pulled yet. The first
          pull collects up to 90 days of history and runs early each morning.
        </Text>
      );
    }
    return <Text>No transactions in this range.</Text>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Date</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>Check #</TableHeader>
            <TableHeader className="text-right">Amount</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((t) => {
            const amt = formatDirectionalAmount(t.amount, t.direction);
            return (
              <TableRow key={t.transaction_id}>
                <TableCell>{formatDate(t.transaction_date)}</TableCell>
                <TableCell>{t.description || '-'}</TableCell>
                <TableCell>{t.check_number || '-'}</TableCell>
                <TableCell
                  className={clsx(
                    'text-right tabular-nums',
                    amountClass(amt.credit)
                  )}
                >
                  {amt.text}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const PendingTable: React.FC<{ rows: PendingTransactionReturn[] | null }> = ({
  rows,
}) => {
  if (!rows || rows.length === 0) {
    return <Text>No pending transactions.</Text>;
  }
  // Columbia echoes the account's current balance on every pending row
  // (identical), so show it once here rather than as a confusing per-row
  // column — pending items haven't settled, so there's no running balance.
  const currentBalance = rows.find(
    (r) => r.current_balance !== null && r.current_balance !== undefined
  )?.current_balance;
  return (
    <div className="overflow-x-auto">
      {currentBalance !== null && currentBalance !== undefined && (
        <Text className="mb-3 text-sm">
          Current balance:{' '}
          <span className="font-medium tabular-nums">
            {formatCurrency(currentBalance)}
          </span>
        </Text>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Date</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader className="text-right">Amount</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((t, i) => {
            // Pending is still LIVE bank data, typed as a float — this
            // endpoint is deliberately not cut over, because memo-post
            // items are short-lived and nothing is stored to render from.
            // Converted explicitly here so the one float path in this
            // dialog is visible rather than hidden in a union type.
            const amt = formatDirectionalAmount(
              t.amount == null ? null : String(t.amount),
              t.direction
            );
            return (
              <TableRow key={`${t.account_number}-${i}`}>
                <TableCell>{formatDate(t.transaction_date)}</TableCell>
                <TableCell>{t.description || '-'}</TableCell>
                <TableCell
                  className={clsx(
                    'text-right tabular-nums',
                    amountClass(amt.credit)
                  )}
                >
                  {amt.text}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const StatementsTable: React.FC<{
  rows: StatementReturn[] | null;
  downloadingId: string | null;
  onDownload: (s: StatementReturn) => void;
}> = ({ rows, downloadingId, onDownload }) => {
  if (!rows || rows.length === 0) {
    return <Text>No statements available.</Text>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Statement Date</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Format</TableHeader>
            <TableHeader>
              <span className="sr-only">Download</span>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.statement_id}>
              <TableCell>{formatDate(s.statement_date)}</TableCell>
              <TableCell>{s.statement_type || '-'}</TableCell>
              <TableCell>
                {s.file_type ? (
                  <Badge color="zinc">{s.file_type.toUpperCase()}</Badge>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  plain
                  onClick={() => onDownload(s)}
                  disabled={downloadingId === s.statement_id}
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                  {downloadingId === s.statement_id
                    ? 'Downloading…'
                    : 'Download'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default AccountActivityDialog;
